import { useNavigate, useSearch } from "@tanstack/react-router";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/lib/auth";

import type { CashFlowSearch, CashFlowTab } from "..";
import { useCashFlowSummary } from "../api";
import { AdminTab } from "../components/AdminTab";
import { AccuracyTab } from "../components/AccuracyTab";
import { AssumptionsTab } from "../components/AssumptionsTab";
import { HowToTab } from "../components/HowToTab";
import { MappingTab } from "../components/MappingTab";
import { SummaryTab } from "../components/SummaryTab";
import { UploadsTab } from "../components/UploadsTab";
import { WaterfallTab } from "../components/WaterfallTab";
import { WeeklyDetailTab } from "../components/WeeklyDetailTab";
import { DEFAULT_CASH_THRESHOLD } from "../lib";

export function CashFlowPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/cash-flow" }) as CashFlowSearch;
  const canAdmin = useCan("cash_flow.admin");
  // A direct URL hit on ?tab=admin without the permission falls back to the
  // summary instead of a 403 (the upsell_ops config-tab idiom).
  const requested: CashFlowTab = search.tab ?? "summary";
  const tab: CashFlowTab = requested === "admin" && !canAdmin ? "summary" : requested;

  const setSearch = (next: Partial<CashFlowSearch>) =>
    navigate({
      to: "/cash-flow",
      search: (prev: CashFlowSearch) => ({ ...prev, ...next }),
    });

  const { data, isLoading, error } = useCashFlowSummary(search.run);
  const run = data?.run ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Cash Flow</h1>
          <p className="text-sm text-muted-foreground">
            13-week cash flow forecast, runway, and week-by-week performance against it —
            built from bank-tied actuals.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {data && data.runs.length > 0 && (
            <Select
              value={run?.id ?? ""}
              onValueChange={(v) => setSearch({ run: v || undefined })}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Forecast run" />
              </SelectTrigger>
              <SelectContent>
                {data.runs.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label}
                    {r.is_current ? " · current" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {run && (
            <p className="text-xs text-muted-foreground">
              {run.source_file ? `Source: ${run.source_file} · ` : ""}
              prepared {run.prepared_on}
              {run.actuals_through ? ` · actuals through ${run.actuals_through} (bank-tied)` : ""}
            </p>
          )}
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-danger/40 bg-danger-light p-4 text-sm text-danger">
          Failed to load cash flow data: {error.message}
        </div>
      ) : (
        <Tabs
          value={tab}
          onValueChange={(v) => setSearch({ tab: v === "summary" ? undefined : (v as CashFlowTab) })}
        >
          <TabsList variant="underline">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="waterfall">Waterfall</TabsTrigger>
            <TabsTrigger value="weekly">Weekly Detail</TabsTrigger>
            <TabsTrigger value="accuracy">Accuracy</TabsTrigger>
            <TabsTrigger value="uploads">Uploads</TabsTrigger>
            <TabsTrigger value="mapping">Mapping</TabsTrigger>
            <TabsTrigger value="assumptions">Assumptions</TabsTrigger>
            <TabsTrigger value="how-to">How to use</TabsTrigger>
            {canAdmin && <TabsTrigger value="admin">Admin</TabsTrigger>}
          </TabsList>
          <TabsContent value="summary" className="pt-4">
            {isLoading || !data ? (
              <div className="rounded-lg border p-10 text-center text-sm text-muted-foreground">
                Loading forecast…
              </div>
            ) : (
              <SummaryTab summary={data} />
            )}
          </TabsContent>
          <TabsContent value="waterfall" className="pt-4">
            <WaterfallTab weeks={data?.weeks ?? []} />
          </TabsContent>
          <TabsContent value="weekly" className="pt-4">
            <WeeklyDetailTab
              cashThreshold={data?.settings?.cash_threshold ?? DEFAULT_CASH_THRESHOLD}
            />
          </TabsContent>
          <TabsContent value="accuracy" className="pt-4">
            <AccuracyTab />
          </TabsContent>
          <TabsContent value="uploads" className="pt-4">
            <UploadsTab />
          </TabsContent>
          <TabsContent value="mapping" className="pt-4">
            <MappingTab />
          </TabsContent>
          <TabsContent value="assumptions" className="pt-4">
            <AssumptionsTab />
          </TabsContent>
          <TabsContent value="how-to" className="pt-4">
            <HowToTab />
          </TabsContent>
          {canAdmin && (
            <TabsContent value="admin" className="pt-4">
              {data && <AdminTab settings={data.settings} />}
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}
